import { useCallback } from "react";
import { useTranslations } from "use-intl";

import { formatTranslatedCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import type { CuratedCrate } from "@garimpa/lib/curatedCrates";

type LooseTranslator = ((key: never, values?: never) => string) & {
  has?: (key: never) => boolean;
};

export interface CuratedCrateNameTranslators {
  /** The `garimpos` namespace. */
  garimpos: LooseTranslator;
  /** The `shared.genres` namespace. */
  genres: LooseTranslator;
  /** The `shared.styles` namespace. */
  styles: LooseTranslator;
}

/** "60"…"90" before 2000, then the full year ("2000", "2010"), as the offers page words it. */
export function curatedCrateDecadeLabel(startYear: number): string {
  return startYear < 2000 ? String(startYear % 100).padStart(2, "0") : String(startYear);
}

export function formatCuratedCrateName(
  crate: CuratedCrate,
  translators: CuratedCrateNameTranslators,
): string {
  const taxonomy = formatTranslatedCatalogTaxonomyLabel(
    crate.taxonomy.key,
    crate.taxonomy.kind === "genre" ? translators.genres : translators.styles,
  );
  return translators.garimpos(
    "crateName" as never,
    {
      taxonomy,
      decade: curatedCrateDecadeLabel(crate.startYear),
    } as never,
  );
}

export function useCuratedCrateName(): (crate: CuratedCrate) => string {
  const garimpos = useTranslations("garimpos");
  const genres = useTranslations("shared.genres");
  const styles = useTranslations("shared.styles");
  return useCallback(
    (crate: CuratedCrate) => formatCuratedCrateName(crate, { garimpos, genres, styles }),
    [garimpos, genres, styles],
  );
}
